import React, { useEffect, useState } from "react";
import { Modal } from "../../../components/Modal";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import { ModoHistoriaService } from "../../../services/admin/modo-historia";

interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItems: any[];
  onSuccess: () => void;
  mundos: any[];
  etapas: any[];
}

export const BulkEditModal: React.FC<BulkEditModalProps> = ({
  isOpen,
  onClose,
  selectedItems,
  onSuccess,
  mundos,
  etapas,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedMundo, setSelectedMundo] = useState<string>("");
  const [selectedEtapa, setSelectedEtapa] = useState<string>("");
  const [etapasDelMundo, setEtapasDelMundo] = useState<any[]>([]);

  // Cargar etapas cuando cambia el mundo seleccionado
  useEffect(() => {
    if (selectedMundo) {
      const mundoId = parseInt(selectedMundo);
      const etapasFiltradas = etapas.filter(
        (etapa) => etapa.mundoId === mundoId
      );
      setEtapasDelMundo(etapasFiltradas);
      setSelectedEtapa(""); // Resetear etapa cuando cambia mundo
    } else {
      setEtapasDelMundo([]);
      setSelectedEtapa("");
    }
  }, [selectedMundo, etapas]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedMundo && !selectedEtapa) {
      alert("Por favor selecciona al menos un mundo o una etapa");
      return;
    }

    setLoading(true);
    try {
      // Preparar los datos de actualización
      const updateData: any = {};

      if (selectedMundo) {
        updateData.mundoHistoriaId = parseInt(selectedMundo);
      }

      if (selectedEtapa) {
        updateData.etapaHistoriaId = parseInt(selectedEtapa);
      }

      // Actualizar cada pregunta seleccionada
      const updatePromises = selectedItems.map((item) =>
        ModoHistoriaService.updatePreguntaOficial(
          item.idPreguntaOficial,
          updateData
        )
      );

      await Promise.all(updatePromises);

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error en edición masiva:", error);
      alert(
        "Error al actualizar las preguntas: " +
          (error.message || "Error desconocido")
      );
    } finally {
      setLoading(false);
    }
  };

  const getMundoName = (mundoId: number) => {
    const mundo = mundos.find((m) => m.idMundoHistoria === mundoId);
    return mundo?.nombre || "N/A";
  };

  const getEtapaName = (etapaId: number) => {
    const etapa = etapas.find((e) => e.idEtapaHistoria === etapaId);
    return etapa?.nombre || "N/A";
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edición Masiva">
      <div className="space-y-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-medium text-blue-900 mb-2">
            Preguntas seleccionadas ({selectedItems.length})
          </h3>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {selectedItems.map((item, index) => (
              <div
                key={item.idPreguntaOficial}
                className="text-sm text-blue-800"
              >
                {index + 1}. {item.contenido.substring(0, 60)}...
                <span className="text-blue-600 ml-2">
                  (Mundo: {getMundoName(item.mundoHistoriaId)}, Etapa:{" "}
                  {getEtapaName(item.etapaHistoriaId)})
                </span>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="mundo">Mundo (opcional)</Label>
            <select
              id="mundo"
              value={selectedMundo}
              onChange={(e) => setSelectedMundo(e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="">Seleccionar mundo</option>
              {mundos.map((mundo) => (
                <option
                  key={mundo.idMundoHistoria}
                  value={mundo.idMundoHistoria}
                >
                  {mundo.nombre}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="etapa">Etapa (opcional)</Label>
            <select
              id="etapa"
              value={selectedEtapa}
              onChange={(e) => setSelectedEtapa(e.target.value)}
              className="w-full p-2 border rounded"
              disabled={!selectedMundo}
            >
              <option value="">
                {selectedMundo
                  ? "Seleccionar etapa"
                  : "Primero selecciona un mundo"}
              </option>
              {etapasDelMundo.map((etapa) => (
                <option
                  key={etapa.idEtapaHistoria}
                  value={etapa.idEtapaHistoria}
                >
                  {etapa.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Confirmación requerida
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Esta acción actualizará {selectedItems.length} pregunta(s):
                    {selectedMundo && (
                      <span className="block">
                        • Mundo:{" "}
                        {
                          mundos.find(
                            (m) => m.idMundoHistoria === parseInt(selectedMundo)
                          )?.nombre
                        }
                      </span>
                    )}
                    {selectedEtapa && (
                      <span className="block">
                        • Etapa:{" "}
                        {
                          etapas.find(
                            (e) => e.idEtapaHistoria === parseInt(selectedEtapa)
                          )?.nombre
                        }
                      </span>
                    )}
                    Esta operación no se puede deshacer.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading
                ? "Actualizando..."
                : `Actualizar ${selectedItems.length} pregunta(s)${
                    selectedMundo || selectedEtapa
                      ? ` (${selectedMundo ? "mundo" : ""}${
                          selectedMundo && selectedEtapa ? " y " : ""
                        }${selectedEtapa ? "etapa" : ""})`
                      : ""
                  }`}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
